<template>
	<div class="table-wrapper" style="inset: 0">
		<div ref="table" class="table-container" @scroll="updateShadows">
			<div
				class="vertical-scroll-shadow"
				:class="{
					'shadow-top': showTopShadow,
					'shadow-bottom': showBottomShadow,
				}"
			/>

			<div
				class="horizontal-scroll-shadow"
				:class="{
					'shadow-left': showLeftShadow,
					'shadow-right': showRightShadow,
				}"
			/>

			<slot name="decoration" />

			<table class="table table-striped mb-0 text-nowrap">
				<thead>
					<slot name="thead" />
				</thead>
				<tbody>
					<slot name="tbody" />
				</tbody>
			</table>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { onBeforeUnmount, onMounted, ref, useTemplateRef } from "vue";

const showTopShadow = ref<boolean>(false);
const showBottomShadow = ref<boolean>(false);
const showLeftShadow = ref<boolean>(false);
const showRightShadow = ref<boolean>(false);
const resizeObserver = ref<ResizeObserver | null>(null);
const table = useTemplateRef<HTMLElement>("table");

const updateShadows = (): void => {
	if (!table.value) return;

	const scrollLeft = table.value!.scrollLeft;
	const maxScrollLeft = table.value!.scrollWidth - table.value!.clientWidth;

	const scrollTop = table.value!.scrollTop;
	const maxScrollTop = table.value!.scrollHeight - table.value!.clientHeight;

	// Disable both horizontal shadows if there's no horizontal scrollable area.
	if (table.value!.scrollWidth <= table.value!.clientWidth) {
		showLeftShadow.value = false;
		showRightShadow.value = false;
	} else {
		showLeftShadow.value = scrollLeft > 0;
		showRightShadow.value = scrollLeft < maxScrollLeft;
	}

	// Disable both vertical shadows if there's no vertical scrollable area.
	if (table.value!.scrollHeight <= table.value!.clientHeight) {
		showTopShadow.value = false;
		showBottomShadow.value = false;
	} else {
		showTopShadow.value = scrollTop > 0;
		showBottomShadow.value = scrollTop < maxScrollTop;
	}
};

onMounted((): void => {
	// Initial update of shadows.
	updateShadows();

	// Create a ResizeObserver to detect changes in the
	// parent element's width to be able to disable
	// shadows if there's no scrollable area.
	if (table.value!.parentElement) {
		resizeObserver.value = new ResizeObserver(updateShadows);

		resizeObserver.value.observe(table.value!.parentElement);
	}
});

onBeforeUnmount((): void => {
	if (resizeObserver.value) {
		resizeObserver.value.disconnect();
	}
});
</script>

<style lang="scss">
/*
    Note: We won't be able to use this in a
    scoped SFC Vue component.
  */
:root {
	--shadow-transparency: 0.2;
}

.vertical-scroll-shadow.shadow-top::before {
	position: absolute;
	top: 0;
	background-image: linear-gradient(
		to bottom,
		rgba(0, 0, 0, var(--shadow-transparency)),
		rgba(0, 0, 0, 0)
	);
	width: 100%;
	height: 20px;
	pointer-events: none;
	content: "";
}

.vertical-scroll-shadow.shadow-bottom::after {
	position: absolute;
	bottom: 0;
	background-image: linear-gradient(
		to top,
		rgba(0, 0, 0, var(--shadow-transparency)),
		rgba(0, 0, 0, 0)
	);
	width: 100%;
	height: 20px;
	pointer-events: none;
	content: "";
}

.horizontal-scroll-shadow.shadow-left::before {
	position: absolute;
	top: 0;
	bottom: 0;
	left: 0;
	background-image: linear-gradient(
		to right,
		rgba(0, 0, 0, var(--shadow-transparency)),
		rgba(0, 0, 0, 0)
	);
	width: 20px;
	pointer-events: none;
	content: "";
}

.horizontal-scroll-shadow.shadow-right::after {
	position: absolute;
	top: 0;
	right: 0;
	bottom: 0;
	background-image: linear-gradient(
		to left,
		rgba(0, 0, 0, var(--shadow-transparency)),
		rgba(0, 0, 0, 0)
	);
	width: 20px;
	pointer-events: none;
	content: "";
}

.table {
	position: relative;
	z-index: 1;
	border: none;
	border-collapse: collapse;
	width: 100%;
	// border-spacing: 0;
	table-layout: fixed;

	&-wrapper {
		position: relative;
		border: 0.125rem solid #5e5f5f;
	}

	&-container {
		overflow: auto;
	}

	tr {
		border-bottom: 1px solid #ddd;

		&:last-child {
			/* Remove bottom border of the last row */
			border-bottom: none !important;
		}
	}

	td {
		border: none;
	}
}
</style>
